import {
  ActionIcon,
  Badge,
  Button,
  Card,
  createTheme,
  Drawer,
  Input,
  Menu,
  Modal,
  NavLink,
  Notification,
  Paper,
  Popover,
  SegmentedControl,
  Table,
  Tabs,
  ThemeIcon,
  Title,
  Tooltip,
  type MantineColorsTuple,
} from '@mantine/core';

// Brand: Deep Indigo #283593 (primary), Sky Blue #0288D1, Saffron #FF9800.
const brand: MantineColorsTuple = ['#EEF0FB', '#DDE1F5', '#BAC2EA', '#94A1DE', '#7381D2', '#5A69C8', '#4656B8', '#3646A5', '#283593', '#1E2A78'];
const sky: MantineColorsTuple = ['#E6F5FC', '#C7E8F8', '#93D2F1', '#5BBBE9', '#2FA6DF', '#1497D6', '#0288D1', '#0277BD', '#01659F', '#014E7C'];
// Filled components use shade 8 (the primary shade), so shade 8 is a vivid saffron, not brown.
const saffron: MantineColorsTuple = ['#FFF6E8', '#FFE9C7', '#FFD594', '#FFC05C', '#FFAE2E', '#FFA114', '#FF9800', '#F28B00', '#E07B00', '#B86200'];
// Slate neutrals: cooler and calmer than default greys. Index 6 is the "dimmed" text colour.
const gray: MantineColorsTuple = ['#F8FAFC', '#F1F5F9', '#E9EEF4', '#E2E8F0', '#CBD5E1', '#94A3B8', '#64748B', '#475569', '#334155', '#0F172A'];

// Latin: DM Sans. Devanagari: "UR Devanagari" (Poppins Devanagari, see styles.css). System fonts are fallbacks.
const FONT =
  '"DM Sans Variable", "UR Devanagari", "DM Sans", system-ui, -apple-system, "Segoe UI", Roboto, "Noto Sans Devanagari", "Nirmala UI", "Kohinoor Devanagari", Mangal, sans-serif';

export const theme = createTheme({
  primaryColor: 'brand',
  primaryShade: { light: 8, dark: 6 },
  colors: { brand, sky, saffron, gray },
  black: '#0F172A',
  fontFamily: FONT,
  fontFamilyMonospace: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  headings: {
    fontFamily: FONT,
    fontWeight: '700',
    sizes: {
      h1: { fontSize: '2rem', lineHeight: '1.2' },
      h2: { fontSize: '1.5rem', lineHeight: '1.25' },
      h3: { fontSize: '1.25rem', lineHeight: '1.3' },
      h4: { fontSize: '1.0625rem', lineHeight: '1.4' },
      h5: { fontSize: '0.9375rem', lineHeight: '1.4' },
    },
  },
  fontSizes: { xs: '0.75rem', sm: '0.875rem', md: '0.9375rem', lg: '1.0625rem', xl: '1.25rem' },
  radius: { xs: '4px', sm: '6px', md: '10px', lg: '14px', xl: '20px' },
  defaultRadius: 'md',
  spacing: { xs: '0.5rem', sm: '0.75rem', md: '1rem', lg: '1.5rem', xl: '2rem' },
  shadows: {
    xs: '0 1px 2px 0 rgb(15 23 42 / 0.05)',
    sm: '0 1px 3px 0 rgb(15 23 42 / 0.07), 0 1px 2px -1px rgb(15 23 42 / 0.05)',
    md: '0 4px 12px -2px rgb(15 23 42 / 0.08), 0 2px 4px -2px rgb(15 23 42 / 0.05)',
    lg: '0 12px 24px -6px rgb(15 23 42 / 0.12), 0 4px 8px -4px rgb(15 23 42 / 0.06)',
    xl: '0 24px 48px -12px rgb(15 23 42 / 0.18)',
  },
  cursorType: 'pointer',
  focusRing: 'auto',
  components: {
    Title: Title.extend({ styles: { root: { letterSpacing: '-0.02em' } } }),
    Button: Button.extend({
      defaultProps: { radius: 'md' },
      styles: { root: { fontWeight: 600, letterSpacing: '-0.005em' } },
    }),
    ActionIcon: ActionIcon.extend({ defaultProps: { radius: 'md', variant: 'subtle', color: 'gray' } }),
    Card: Card.extend({ defaultProps: { radius: 'lg', withBorder: true, padding: 'lg', shadow: 'xs' } }),
    Paper: Paper.extend({ defaultProps: { radius: 'lg' } }),
    Badge: Badge.extend({
      defaultProps: { radius: 'sm', variant: 'light' },
      styles: { root: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 } },
    }),
    Table: Table.extend({
      defaultProps: { verticalSpacing: 'sm', horizontalSpacing: 'md' },
      styles: {
        th: {
          fontSize: '0.75rem',
          fontWeight: 600,
          color: 'var(--mantine-color-gray-6)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          background: 'var(--mantine-color-gray-0)',
          whiteSpace: 'nowrap',
        },
        td: { fontSize: '0.875rem' },
      },
    }),
    Tabs: Tabs.extend({ styles: { tab: { fontWeight: 600, fontSize: '0.875rem' } } }),
    NavLink: NavLink.extend({
      defaultProps: { variant: 'light' },
      styles: { root: { borderRadius: 10, minHeight: 40 }, label: { fontWeight: 500, fontSize: '0.875rem' } },
    }),
    Input: Input.extend({ styles: { input: { borderColor: 'var(--mantine-color-gray-4)' } } }),
    InputWrapper: Input.Wrapper.extend({
      styles: { label: { fontWeight: 600, fontSize: '0.8125rem', marginBottom: 6 }, description: { marginBottom: 6 } },
    }),
    SegmentedControl: SegmentedControl.extend({ defaultProps: { radius: 'md' }, styles: { label: { fontWeight: 600 } } }),
    ThemeIcon: ThemeIcon.extend({ defaultProps: { radius: 'md', variant: 'light' } }),
    Tooltip: Tooltip.extend({ defaultProps: { radius: 'md', withArrow: true, openDelay: 200 } }),
    Menu: Menu.extend({ defaultProps: { radius: 'md', shadow: 'lg', withinPortal: true } }),
    Popover: Popover.extend({ defaultProps: { radius: 'md', shadow: 'lg' } }),
    Modal: Modal.extend({
      defaultProps: { radius: 'lg', centered: true, overlayProps: { backgroundOpacity: 0.45, blur: 2 } },
      styles: { title: { fontWeight: 700, fontSize: '1.0625rem' }, header: { paddingBottom: 8 } },
    }),
    Drawer: Drawer.extend({ defaultProps: { overlayProps: { backgroundOpacity: 0.4, blur: 2 } }, styles: { title: { fontWeight: 700 } } }),
    Notification: Notification.extend({ defaultProps: { radius: 'md', withBorder: true } }),
  },
});
